import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useWishlist, WishlistItem } from '../../contexts/WishlistContext';
import { formatPrice } from '../../utils/currency';
import Button from '../ui/Button';
import Input from '../ui/Input';
import LoadingSpinner from '../ui/LoadingSpinner';
import WishlistCard from './WishlistCard';
import WishlistForm from './WishlistForm';

const WishlistDashboard: React.FC = () => {
  const { user } = useAuth();
  const isDemoUser = user?.username === 'Demo';
  const { 
    collection, 
    stats, 
    error, 
    filters, 
    sorting, 
    pageSize,
    fetchWishlistItems, 
    fetchStats, 
    deleteWishlistItem, 
    moveToCollection,
    copyWishlistDetails,
    setFilters,
    setSorting,
    setPageSize
  } = useWishlist();

  const [isLoading, setIsLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState<WishlistItem | null>(null);
  const [searchQuery, setSearchQuery] = useState(filters.search || '');
  const [localFilters, setLocalFilters] = useState(filters);
  const [localSorting, setLocalSorting] = useState(sorting);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const { token } = useAuth();

  // Fetch data on mount
  useEffect(() => {
    if (token) {
      setIsLoading(true);
      fetchWishlistItems();
      fetchStats();
      setIsLoading(false);
    }
  }, [token]);

  // Handle search with debouncing
  useEffect(() => {
    if (!token) return;
    
    const timeoutId = setTimeout(() => {
      const newFilters = { ...localFilters, search: searchQuery || undefined };
      setLocalFilters(newFilters);
      setFilters(newFilters);
      fetchWishlistItems(newFilters, localSorting);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, token]);

  const handleEdit = (item: WishlistItem) => {
    if (isDemoUser) return;
    setEditingItem(item);
  };

  const handleDelete = async (itemId: number) => {
    if (isDemoUser) return;
    const result = await deleteWishlistItem(itemId);
    if (result.success) {
      console.log('Wishlist item deleted successfully');
    } else {
      console.error('Failed to delete wishlist item:', result.message);
    }
  };

  const handleMoveToCollection = async (itemId: number) => {
    if (isDemoUser) return;
    const result = await moveToCollection(itemId);
    if (result.success) {
      console.log('Item moved to collection successfully');
    } else {
      console.error('Failed to move item to collection:', result.message);
    }
  };

  const handleCopyDetails = (item: WishlistItem) => {
    copyWishlistDetails(item);
    console.log('Wishlist item details copied to clipboard');
  };

  const handleAddNew = () => {
    if (isDemoUser) return;
    setShowAddForm(true);
  };

  const handleFormClose = () => {
    if (isDemoUser) return;
    setShowAddForm(false);
    setEditingItem(null);
  };

  const handleFormSuccess = () => {
    if (isDemoUser) return;
    fetchStats(); // Refresh stats after adding/editing
  };

  const handleFilterChange = (key: string, value: any) => {
    const newFilters = { ...localFilters, [key]: value || undefined };
    setLocalFilters(newFilters);
  };

  const handleSortingChange = (key: string, value: any) => {
    const newSorting = { ...localSorting, [key]: value };
    setLocalSorting(newSorting);
    setSorting(newSorting);
    fetchWishlistItems(localFilters, newSorting);
  };

  const applyFilters = () => {
    setFilters(localFilters);
    fetchWishlistItems(localFilters, localSorting);
    setIsFilterOpen(false);
  };

  const resetFilters = () => {
    const emptyFilters = {};
    setLocalFilters(emptyFilters);
    setSearchQuery('');
    setFilters(emptyFilters);
    fetchWishlistItems(emptyFilters, localSorting);
    setIsFilterOpen(false);
  };

  const activeFilterCount = Object.values(localFilters).filter(v => v !== undefined && v !== null && v !== '').length;

  return (
    <div>
      {/* Add Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 flex justify-end"
      >
        <Button
          onClick={handleAddNew}
          disabled={isDemoUser} // disable if demo user
          title={isDemoUser ? 'Unavailable in Demo' : undefined} // helper text
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add to Wishlist
        </Button>
      </motion.div>

      {/* Stats Cards */}
      {stats && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8"
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.totals.items}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Total Items
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              Total Price
            </div>
            <div className="space-y-1">
              {stats.pricesByCurrency.length > 0 ? (
                stats.pricesByCurrency.length === 1 ? (
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {formatPrice(stats.pricesByCurrency[0].total_price, stats.pricesByCurrency[0].currency)}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-1">
                    {stats.pricesByCurrency.map(price => (
                      <div key={price.currency} className="flex items-center justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">
                          {price.currency}
                        </span>
                        <span className="text-sm font-bold text-green-600 dark:text-green-400">
                          {formatPrice(price.total_price, price.currency)}
                        </span>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                <div className="text-lg font-bold text-gray-400">
                  No prices set
                </div>
              )}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.totals.houses}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Total Houses
            </div>
          </div>
        </motion.div>
      )}

      {/* Error Display */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
        >
          <div className="flex">
            <svg className="w-5 h-5 text-red-400 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Error loading wishlist
              </h3>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                {error}
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Search and Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6"
      >
        {/* Search Bar */}
        <div className="flex flex-col gap-4">
          <div className="flex-1 relative">
            <Input
              type="text"
              placeholder="Search wishlist by name or house..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 items-stretch sm:items-center">
            {/* Sort Dropdown */}
            <div className="flex items-center space-x-2 flex-shrink-0">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 hidden sm:block">
                Sort by:
              </label>
              <select
                value={localSorting.sort_by}
                onChange={(e) => handleSortingChange('sort_by', e.target.value)}
                className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-2 sm:px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1 sm:flex-none"
              >
                <option value="created_at">Date Added</option>
                <option value="name">Name</option>
                <option value="house">House</option>
                <option value="price">Price</option>
                <option value="priority">Priority</option>
              </select>
              <button
                onClick={() => handleSortingChange('sort_order', localSorting.sort_order === 'asc' ? 'desc' : 'asc')}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors duration-200 bg-gray-100 dark:bg-gray-700 rounded-lg flex-shrink-0"
                title={`Currently: ${localSorting.sort_order === 'asc' ? 'Low to High' : 'High to Low'} - Click to toggle`}
              >
                {localSorting.sort_order === 'asc' ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
                  </svg>
                )}
              </button>
            </div>

            {/* Filter Toggle */}
            <Button
              variant="outline"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="relative flex-shrink-0"
              size="sm"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filters
              {activeFilterCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </div>
        </div>

        {/* Filter Panel */}
        <AnimatePresence>
          {isFilterOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="pt-6 border-t border-gray-200 dark:border-gray-700 mt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  {/* House Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      House
                    </label>
                    <input
                      type="text"
                      placeholder="Filter by house..."
                      value={localFilters.house || ''}
                      onChange={(e) => handleFilterChange('house', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  {/* Priority Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Priority
                    </label>
                    <select
                      value={localFilters.priority || ''}
                      onChange={(e) => handleFilterChange('priority', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">All Priorities</option>
                      <option value="high">🔴 High Priority</option>
                      <option value="medium">🟡 Medium Priority</option>
                      <option value="low">🟢 Low Priority</option>
                    </select>
                  </div>

                  {/* Min Price Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Min Price
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={localFilters.min_price || ''}
                      onChange={(e) => handleFilterChange('min_price', e.target.value ? parseFloat(e.target.value) : undefined)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  {/* Max Price Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Max Price
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="999999.99"
                      value={localFilters.max_price || ''}
                      onChange={(e) => handleFilterChange('max_price', e.target.value ? parseFloat(e.target.value) : undefined)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                {/* Toggle Filters */}
                <div className="flex flex-wrap gap-4 mb-6">
                  <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
                    <input
                      type="checkbox"
                      checked={localFilters.is_gift_idea === true}
                      onChange={(e) => handleFilterChange('is_gift_idea', e.target.checked ? true : undefined)}
                      className="rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                    />
                    <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">🎁 Gift ideas only</span>
                  </label>
                </div>

                {/* Filter Actions */}
                <div className="flex items-center space-x-4 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <Button onClick={applyFilters}>
                    Apply Filters
                  </Button>
                  <Button variant="outline" onClick={resetFilters}>
                    Clear All
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Wishlist Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Your Wishlist
            {collection && (
              <span className="ml-2 text-sm font-normal text-gray-600 dark:text-gray-400">
                ({collection.wishlistItems.length} of {collection.pagination.total})
              </span>
            )}
          </h2>
          {collection && collection.pagination.totalPages > 1 && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => fetchWishlistItems(filters, sorting, collection.pagination.page - 1)}
                disabled={collection.pagination.page === 1}
                className="px-3 py-1 rounded border text-sm disabled:opacity-50"
              >
                Previous
              </button>
              {[...Array(collection.pagination.totalPages)].map((_, index) => {
                const pageNum = index + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => fetchWishlistItems(filters, sorting, pageNum)}
                    className={`px-3 py-1 rounded border text-sm ${
                      collection.pagination.page === pageNum 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-white text-gray-700 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => fetchWishlistItems(filters, sorting, collection.pagination.page + 1)}
                disabled={collection.pagination.page === collection.pagination.totalPages}
                className="px-3 py-1 rounded border text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Loading State */}
        {isLoading || !collection ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <LoadingSpinner size="lg" className="mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Loading your wishlist...</p>
            </div>
          </div>
        ) : collection?.wishlistItems.length === 0 ? (
          // Empty State
          <div className="text-center py-16">
            <div className="text-6xl mb-4">💭</div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Your wishlist is empty
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
              Add fragrances you're interested in collecting. Keep track of prices, retailers, and notes for future purchases.
            </p>
            <Button onClick={handleAddNew}>
              Add Your First Item
            </Button>
          </div>
        ) : (
          // Wishlist Grid
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-8">
            <AnimatePresence mode="popLayout">
              {collection?.wishlistItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: -20 }}
                  transition={{ duration: 0.3, delay: index * 0.05, layout: { duration: 0.3 } }}
                >
                  <WishlistCard
                    item={item}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onMoveToCollection={handleMoveToCollection}
                    onCopyDetails={handleCopyDetails}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </motion.div>

      {/* Forms */}
      {(showAddForm || editingItem) && (
        <WishlistForm
          item={editingItem}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};

export default WishlistDashboard;